import React from 'react';
import { Shield, Users, Bell, Settings } from 'lucide-react';

const PrivacyControls = ({ privacy, notifications, onUpdate, isEditing }) => {
  const handlePrivacyChange = (key, value) => {
    const newPrivacy = { ...privacy, [key]: value };
    onUpdate?.({ privacy: newPrivacy });
  };

  const handleNotificationChange = (key, value) => {
    const newNotifications = { ...notifications, [key]: value };
    onUpdate?.({ notifications: newNotifications });
  };

  const notificationSettings = [
    {
      id: 'dailyReminders',
      label: 'Daily Study Reminders',
      description: 'Get reminded to maintain your study streak',
      icon: Bell
    },
    {
      id: 'studyUpdates',
      label: 'Study Plan Updates',
      description: 'Notifications about reading plan progress',
      icon: Settings
    },
    {
      id: 'communityActivity',
      label: 'Community Activity',
      description: 'Updates from people you follow',
      icon: Users
    }
  ];

  return (
    <div className="space-y-6">
      {/* Privacy Settings */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Privacy Settings</h2>
            <p className="text-muted-foreground">Control who can see your content and activity</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Reflection Visibility</label>
            <select
              value={privacy?.reflectionVisibility || 'private'}
              onChange={(e) => handlePrivacyChange('reflectionVisibility', e?.target?.value)}
              disabled={!isEditing}
              className={`block w-full px-3 py-2 border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm ${!isEditing ? 'bg-muted border-border cursor-not-allowed opacity-60' : 'bg-background border-border'}`}
            >
              <option value="private">Private (Only Me)</option>
              <option value="friends">Friends Only</option>
              <option value="public">Public (Everyone)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">Control who can see your verse reflections</p>
          </div>

          {/* Community Interaction Toggle */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Community Interactions</p>
                  <p className="text-sm text-muted-foreground">Allow others to comment and interact</p>
                </div>
              </div>
              <button
                onClick={() => handlePrivacyChange('communityInteractions', !privacy?.communityInteractions)}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer"
                style={{ backgroundColor: privacy?.communityInteractions ? 'var(--color-primary)' : 'var(--color-muted)' }}
                aria-label="Toggle community interactions"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacy?.communityInteractions ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>

          {/* Statistics Visibility Toggle */}
          <div className="bg-muted/40 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-success/10 rounded-full flex items-center justify-center">
                  <Shield className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Show Statistics</p>
                  <p className="text-sm text-muted-foreground">Display your progress stats publicly</p>
                </div>
              </div>
              <button
                onClick={() => handlePrivacyChange('showStats', !privacy?.showStats)}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer"
                style={{ backgroundColor: privacy?.showStats ? 'var(--color-primary)' : 'var(--color-muted)' }}
                aria-label="Toggle show statistics"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacy?.showStats ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Notification Preferences */}
      <div className="bg-card rounded-2xl border border-border shadow-warm-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-bold text-foreground">Notification Preferences</h2>
            <p className="text-muted-foreground">Customize when and how you receive updates</p>
          </div>
        </div>

        <div className="space-y-4">
          {notificationSettings?.map((setting) => (
            <div key={setting?.id} className="bg-muted/40 rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <setting.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{setting?.label}</p>
                    <p className="text-sm text-muted-foreground">{setting?.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleNotificationChange(setting?.id, !notifications?.[setting?.id])}
                  className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer"
                  style={{ backgroundColor: notifications?.[setting?.id] ? 'var(--color-primary)' : 'var(--color-muted)' }}
                  aria-label={setting?.label}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notifications?.[setting?.id] ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Notification Schedule */}
        <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
          <h4 className="font-medium text-foreground mb-3">Reminder Schedule</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Morning Reminder</label>
              <select
                disabled={!isEditing || !notifications?.dailyReminders}
                className={`block w-full px-3 py-2 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm ${!isEditing || !notifications?.dailyReminders ? 'bg-muted cursor-not-allowed opacity-60' : 'bg-background'}`}
              >
                <option value="07:00">7:00 AM</option>
                <option value="08:00">8:00 AM</option>
                <option value="09:00">9:00 AM</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Evening Reminder</label>
              <select
                disabled={!isEditing || !notifications?.dailyReminders}
                className={`block w-full px-3 py-2 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm ${!isEditing || !notifications?.dailyReminders ? 'bg-muted cursor-not-allowed opacity-60' : 'bg-background'}`}
              >
                <option value="18:00">6:00 PM</option>
                <option value="19:00">7:00 PM</option>
                <option value="20:00">8:00 PM</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      {/* Privacy Summary */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl shadow-warm p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
            <Shield className="w-4 h-4 text-primary-foreground" />
          </div>
          <h3 className="font-semibold text-foreground">Privacy Summary</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <p className="font-medium text-foreground">Reflections</p>
            <p className="text-primary capitalize">{privacy?.reflectionVisibility || 'Private'}</p>
          </div>
          <div className="text-center">
            <p className="font-medium text-foreground">Interactions</p>
            <p className="text-primary">{privacy?.communityInteractions ? 'Enabled' : 'Disabled'}</p>
          </div>
          <div className="text-center">
            <p className="font-medium text-foreground">Statistics</p>
            <p className="text-primary">{privacy?.showStats ? 'Public' : 'Hidden'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyControls;